import React, { useState } from "react";

// ASSETS

import mapIcon from "assets/svg/MapIcon.svg";
import mapIconSelected from "assets/svg/MapIconSelected.svg";
import manIcon from "assets/svg/ManIcon.svg";
import manIconSelected from "assets/svg/ManIconSelected.svg";
import phoneIcon from "assets/svg/PhoneIcon.svg";
import phoneIconSelected from "assets/svg/PhoneIconSelected.svg";
import searchIcon from "assets/svg/SearchIcon.svg";
import searchIconSelected from "assets/svg/SearchIcon.svg";

// STYLES
import {
  ButtonsBarWrapper,
  ButtonsBarContainer,
  HoverText,
  WrapperLanguage,
} from "./ButtonsBarAtomStyled";

// LIBRARIES

import { useNavigate } from "react-router";
import { useMediaQuery } from "react-responsive";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

// MISC

import { IconButtonAtomProps } from "../IconButtonAtom/IconButtonAtomModel";
import { UIState } from "./ButtonsBarAtomModel";
import { TextType } from "components/Atoms/CustomText/CustomTextModel";
import { COLORS } from "constants/variables";
import { appRoutes } from "config/AppRoutesConfig";

// REDUX

import { selectLanguage } from "slices/LanguageSlice/languageSlice";
import { accessibilityBiggerCursorSelector } from "slices/UISlice/uiSliceSelectors";

// COMPONENTS

import AccesibilityModal from "./AccesilibilyModal/AccesibilityModal";
import LanguageModal from "./LanguageModal/LanguageModal";
import CustomText from "components/Atoms/CustomText/CustomText";
import IconButtonAtom from "../IconButtonAtom/IconButtonAtom";

const ButtonsBarAtom: React.FC = () => {
  // PROPERTIES

  // STATE CONSTANTS

  const [uiState, setUiState] = useState<UIState>({
    selectedIconIndex: null,
    isLanguageModalOpen: false,
    isModalOpen: false,
  });
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  // LIBRARY CONSTANTS

  const navigate = useNavigate();
  const { t } = useTranslation();
  const isMobile = useMediaQuery({ maxWidth: 1280 });
  const language = useSelector(selectLanguage);
  const biggerCursor = useSelector(accessibilityBiggerCursorSelector);
  const iconsList: IconButtonAtomProps[] = [
    {
      id: 0,
      icon: searchIcon,
      isSelected: uiState.selectedIconIndex === 0,
      iconHovered: searchIconSelected,
      shouldNavigateOn: true,
      isText: false,
      path: appRoutes.search,
      hoverText: t("components.header.search"),
      style: {
        width: '20px',
        height: '20px',
        filter: 'invert(87%) sepia(77%) saturate(1000%) hue-rotate(353deg) brightness(103%) contrast(97%)' // COLORS.Yellow
      },
      hoverStyle: {
        filter: 'invert(15%) sepia(6%) saturate(319%) hue-rotate(202deg) brightness(95%) contrast(86%)' // COLORS.RaisinBlack on hover
      }
    },
    {
      id: 1,
      icon: mapIcon,
      isSelected: uiState.selectedIconIndex === 1,
      iconHovered: mapIconSelected,
      shouldNavigateOn: true,
      isText: false,
      path: appRoutes.map + "/unitati-de-primiri-urgente-9",
      hoverText: t("components.header.map"),
    },

    {
      id: 2,
      isText: true,
      text: language.toUpperCase(),
      isSelected: uiState.selectedIconIndex === 2,
      shouldNavigateOn: false,
      hoverText: t("components.header.language"),
    },
    {
      id: 3,
      icon: manIcon,
      isSelected: uiState.selectedIconIndex === 3,
      iconHovered: manIconSelected,
      shouldNavigateOn: false,
      isText: false,
      hoverText: t("components.header.accesibility"),
    },
    {
      id: 4,
      icon: phoneIcon,
      isSelected: uiState.selectedIconIndex === 4,
      iconHovered: phoneIconSelected,
      shouldNavigateOn: true,
      isText: false,
      path: appRoutes.contact,
      hoverText: t("components.header.contact"),
    },
  ];

  // API REQUESTS

  // LIFE CYCLE

  // EVENT HANDLERS
  const handleIconClick = (iconProps: IconButtonAtomProps) => {
    const newState = {
      selectedIconIndex: iconProps.id,
      isModalOpen: iconProps.id === 3,
      isLanguageModalOpen: iconProps.id === 2,
      shouldNavigateOn: iconProps.shouldNavigateOn,
      path: iconProps.path,
    };

    setUiState(newState);

    if (iconProps.shouldNavigateOn) {
      navigate(iconProps.path);
    }
  };
  const closeAccesibilityModal = () =>
    setUiState((prev) => ({
      ...prev,
      isModalOpen: false,
      selectedIconIndex: null,
    }));
  const closeLanguageModal = () =>
    setUiState((prev) => ({
      ...prev,
      isLanguageModalOpen: false,
      selectedIconIndex: null,
    }));

  return (
    <ButtonsBarWrapper>
      <ButtonsBarContainer>
        {iconsList.map((iconProps, index) =>
          iconProps.isText === false ? (
            <IconButtonAtom
              key={index}
              {...iconProps}
              isSelected={
                iconProps.shouldNavigateOn
                  ? window.location.pathname.includes(iconProps.path)
                  : uiState.selectedIconIndex === index
              }
              hoverText={iconProps.hoverText}
              onClick={() => handleIconClick(iconProps)}
            />
          ) : (
            <WrapperLanguage
              biggerCursor={biggerCursor}
              key={index}
              onClick={() => handleIconClick(iconProps)}
              tabIndex={0}
              isSelected={
                iconProps.shouldNavigateOn
                  ? window.location.pathname.includes(iconProps.path)
                  : uiState.selectedIconIndex === index
              }
              onMouseEnter={() => setHoveredIndex(index)}
              onMouseLeave={() => setHoveredIndex(null)}
            >
              <CustomText
                text={language.toUpperCase()}
                type={TextType.H4}
                color={
                  hoveredIndex === index ? COLORS.RaisinBlack : COLORS.Yellow
                }
              />

              {!isMobile && (
                <HoverText isMobile={isMobile}>
                  <CustomText text={iconProps.hoverText} type={TextType.H5} />
                </HoverText>
              )}
            </WrapperLanguage>
          )
        )}
      </ButtonsBarContainer>
      {
        <LanguageModal
          isOpenProps={uiState.isLanguageModalOpen}
          setIsOpenProps={closeLanguageModal}
          options={[
            { label: "Romana", value: "ro" },
            { label: "English", value: "en" },
          ]}
        />
      }
      <AccesibilityModal
        isOpen={uiState.isModalOpen}
        onClose={closeAccesibilityModal}
      />
    </ButtonsBarWrapper>
  );
};

export default ButtonsBarAtom;
